import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    TextField,
    Typography
} from "@mui/material";
import { Globe, Pin, X } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import { useAccessManifest } from "@/auth/AccessManifestProvider";
import { CONTEXT_MESH_CAPABILITIES } from "@/auth/accessNavigation";
import {
    ContextMeshAssignmentTarget,
    FileAssignments,
    ProductAssignmentTarget,
    listFileAssignmentTargets,
    updateFileAssignments
} from "@/api/fileAssignments";
import { VectorFile } from "@/api/models/vector_file";
import { FileTypeTile, T } from "./filesV6";

export interface ScopeChanges {
    added_project_ids: number[];
    removed_project_ids: number[];
    added_feature_ids: number[];
    removed_feature_ids: number[];
    added_context_mesh_ids: string[];
    removed_context_mesh_ids: string[];
}

export interface SavedScopeData {
    fileid: number;
    is_global: boolean;
    company_id: number | null;
    project_ids: number[];
    context_mesh_ids: string[];
    feature_ids: number[];
    changes: ScopeChanges;
    refresh_delayed: boolean;
    refresh_message: string | null;
}

export interface LegacyFileScope {
    global: { company_id: number; company_name: string } | null;
    projects: Array<{ project_id: number; project_name: string }>;
    features: Array<{ feature_id: number; feature_name: string }>;
}

interface FileScopeModalProps {
    open: boolean;
    file: VectorFile | null;
    /** Legacy Product scopes are accepted while non-shared callers migrate. */
    currentScope?: LegacyFileScope | null;
    currentAssignments?: FileAssignments | null;
    onClose: () => void;
    onSave: (savedScope: SavedScopeData) => void;
    onRefreshDelayed?: (message: string) => void;
    /** Preselect the origin when a newly uploaded file has no assignments yet. */
    initialProjectId?: number;
    initialContextMeshId?: string;
}

function selectedIds<T extends string | number>(values: Set<T>): T[] {
    return Array.from(values);
}

const FileScopeModal: React.FC<FileScopeModalProps> = ({
    open,
    file,
    currentScope,
    currentAssignments,
    onClose,
    onSave,
    onRefreshDelayed,
    initialProjectId,
    initialContextMeshId
}) => {
    const { getToken } = useAuth();
    const { manifest, hasCapability } = useAccessManifest();
    const canAssignProducts = manifest?.entitlements.product_studio === true;
    const canAssignContextMeshes = manifest?.entitlements.context_mesh === true &&
        hasCapability(CONTEXT_MESH_CAPABILITIES.manageFiles);

    const [products, setProducts] = useState<ProductAssignmentTarget[]>([]);
    const [contextMeshes, setContextMeshes] = useState<ContextMeshAssignmentTarget[]>([]);
    const [canAssignAllProducts, setCanAssignAllProducts] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isGlobal, setIsGlobal] = useState(false);
    const [pickedProducts, setPickedProducts] = useState<Set<number>>(new Set());
    const [pickedContextMeshes, setPickedContextMeshes] = useState<Set<string>>(new Set());
    const [hasFetchedTargets, setHasFetchedTargets] = useState(false);

    useEffect(() => {
        if (!open) return;
        const productMode = currentAssignments?.products.mode;
        const productIds = currentAssignments?.products.ids ?? currentScope?.projects.map(p => p.project_id) ?? [];
        const meshIds = currentAssignments?.contextMeshes.ids ?? [];
        setIsGlobal(productMode === "all" || (!currentAssignments && Boolean(currentScope?.global)));
        setPickedProducts(new Set(
            productIds.length > 0 ? productIds : initialProjectId != null ? [initialProjectId] : []
        ));
        setPickedContextMeshes(new Set(
            meshIds.length > 0 ? meshIds : initialContextMeshId ? [initialContextMeshId] : []
        ));
        setLoadError(null);
    }, [open, currentAssignments, currentScope, initialProjectId, initialContextMeshId]);

    useEffect(() => {
        if (!open) {
            setHasFetchedTargets(false);
            return;
        }
        if (hasFetchedTargets) return;
        let cancelled = false;
        void (async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const token = await getToken();
                if (!token) throw new Error("Authentication is required to load assignment targets.");
                const targets = await listFileAssignmentTargets(token);
                if (cancelled) return;
                setProducts(canAssignProducts ? targets.products : []);
                setCanAssignAllProducts(canAssignProducts && targets.canAssignAllProducts);
                setContextMeshes(
                    canAssignContextMeshes
                        ? targets.contextMeshes.filter(mesh => mesh.accessRole === "owner" || mesh.accessRole === "editor")
                        : []
                );
            } catch (error) {
                if (!cancelled) {
                    setLoadError(error instanceof Error ? error.message : "Unable to load assignment targets.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setHasFetchedTargets(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, hasFetchedTargets, getToken, canAssignProducts, canAssignContextMeshes]);

    const selectedProducts = useMemo(
        () => products.filter(product => pickedProducts.has(product.id)),
        [products, pickedProducts]
    );
    const selectedContextMeshes = useMemo(
        () => contextMeshes.filter(mesh => pickedContextMeshes.has(mesh.id)),
        [contextMeshes, pickedContextMeshes]
    );
    const productDomainIsEditable = canAssignProducts && (!isGlobal || canAssignAllProducts);
    const hasEditableDomain = productDomainIsEditable || canAssignContextMeshes;

    const handleSave = async () => {
        if (!file || saving || loading || loadError) return;
        setSaving(true);
        try {
            const token = await getToken();
            if (!token) throw new Error("Authentication is required to save assignments.");

            const originalProductIds = new Set(
                currentAssignments?.products.ids ?? currentScope?.projects.map(project => project.project_id) ?? []
            );
            const originalContextMeshIds = new Set(currentAssignments?.contextMeshes.ids ?? []);
            const productIds = selectedIds(pickedProducts);
            const contextMeshIds = selectedIds(pickedContextMeshes);
            const result = await updateFileAssignments(token, file.fileid, {
                ...(productDomainIsEditable
                    ? { products: { mode: isGlobal ? "all" as const : "selected" as const, ids: isGlobal ? [] : productIds } }
                    : {}),
                ...(canAssignContextMeshes ? { contextMeshes: { ids: contextMeshIds } } : {})
            });

            const saved: SavedScopeData = {
                fileid: file.fileid,
                is_global: isGlobal,
                company_id: currentScope?.global?.company_id ?? null,
                project_ids: isGlobal ? [] : productIds,
                context_mesh_ids: contextMeshIds,
                feature_ids: [],
                changes: {
                    added_project_ids: productIds.filter(id => !originalProductIds.has(id)),
                    removed_project_ids: Array.from(originalProductIds).filter(id => !productIds.includes(id)),
                    added_feature_ids: [],
                    removed_feature_ids: (currentScope?.features ?? []).map(feature => feature.feature_id),
                    added_context_mesh_ids: contextMeshIds.filter(id => !originalContextMeshIds.has(id)),
                    removed_context_mesh_ids: Array.from(originalContextMeshIds).filter(id => !contextMeshIds.includes(id))
                },
                refresh_delayed: result.refresh.delayed,
                refresh_message: result.refresh.message
            };
            onSave(saved);
            if (result.refresh.delayed) {
                onRefreshDelayed?.(result.refresh.message || "Assignments were saved, but score refresh is delayed.");
            }
            onClose();
        } catch (error) {
            console.error("Error saving file assignments:", error);
            setLoadError(error instanceof Error ? error.message : "Unable to save file assignments.");
        } finally {
            setSaving(false);
        }
    };

    const assignedCount = (isGlobal ? 1 : pickedProducts.size) + pickedContextMeshes.size;

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            aria-labelledby="file-scope-title"
            slotProps={{ backdrop: { sx: { backgroundColor: "rgba(15,18,32,0.42)", backdropFilter: "blur(2px)" } } }}
            PaperProps={{
                sx: {
                    width: 640,
                    maxWidth: "92vw",
                    maxHeight: 720,
                    borderRadius: "18px",
                    boxShadow: "0 30px 80px rgba(15,18,32,0.4)",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    backgroundColor: T.bgPaper
                }
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "18px 22px", borderBottom: `1px solid ${T.divider}` }}>
                <FileTypeTile filename={file?.filename} size={36} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography id="file-scope-title" sx={{ fontSize: 16, fontWeight: 600, color: T.fg1 }}>
                        Where is this file used?
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: T.fg4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {file?.filename}
                    </Typography>
                </Box>
                <Box
                    component="button"
                    onClick={onClose}
                    disabled={saving}
                    aria-label="Close"
                    sx={{
                        width: 30, height: 30, borderRadius: "8px", border: `1px solid ${T.divider}`,
                        backgroundColor: T.bgPaper, color: T.fg3, cursor: "pointer", p: 0,
                        display: "inline-flex", alignItems: "center", justifyContent: "center"
                    }}
                >
                    <X size={15} />
                </Box>
            </Box>

            {canAssignProducts && (canAssignAllProducts || isGlobal) && (
                <Box
                    role="switch"
                    aria-checked={isGlobal}
                    aria-label="All products"
                    aria-disabled={!canAssignAllProducts}
                    tabIndex={canAssignAllProducts ? 0 : -1}
                    onClick={() => canAssignAllProducts && setIsGlobal(value => !value)}
                    onKeyDown={event => {
                        if (canAssignAllProducts && (event.key === " " || event.key === "Enter")) {
                            event.preventDefault();
                            setIsGlobal(value => !value);
                        }
                    }}
                    sx={{
                        display: "flex", alignItems: "center", gap: "12px", p: "14px 22px",
                        cursor: canAssignAllProducts ? "pointer" : "default",
                        opacity: canAssignAllProducts ? 1 : 0.65,
                        backgroundColor: isGlobal ? T.spineSoft : "transparent", borderBottom: `1px solid ${T.divider}`,
                        "&:focus-visible": { outline: `2px solid ${T.primary}`, outlineOffset: "-2px" }
                    }}
                >
                    <Globe size={16} color={isGlobal ? T.spineDeep : T.fg3} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: isGlobal ? T.spineDeep : T.fg1 }}>All products</Typography>
                        <Typography sx={{ fontSize: 11.5, color: T.fg4 }}>
                            Every Product Hub product—current and future—can use it. Standalone Context Meshes are selected separately.
                        </Typography>
                    </Box>
                    <Box component="span" sx={{ width: 38, height: 22, borderRadius: 999, backgroundColor: isGlobal ? T.spineDeep : T.divider, position: "relative" }}>
                        <Box component="span" sx={{ position: "absolute", top: 2, left: isGlobal ? 18 : 2, width: 18, height: 18, borderRadius: 999, backgroundColor: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.25)" }} />
                    </Box>
                </Box>
            )}

            <Box sx={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", p: "18px 22px", display: "flex", flexDirection: "column", gap: 2.25 }}>
                {loadError && <Alert severity="error">{loadError}</Alert>}
                {loading && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontSize: 12.5, color: T.fg4 }}>Loading available assignments…</Typography>
                    </Box>
                )}
                {!loading && isGlobal && canAssignProducts && !canAssignAllProducts && (
                    <Alert severity="info">
                        The All products assignment is read-only because you cannot manage every Product Hub product.
                    </Alert>
                )}
                {canAssignProducts && (
                    <Box>
                        <Typography component="label" htmlFor="file-product-assignments" sx={{ display: "block", fontSize: 12.5, fontWeight: 700, color: T.fg2, mb: 0.75 }}>
                            Products
                        </Typography>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            disabled={loading || isGlobal}
                            options={products}
                            value={selectedProducts}
                            onChange={(_, values) => setPickedProducts(new Set(values.map(value => value.id)))}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={option => option.name}
                            noOptionsText="No authorized products available"
                            renderTags={(values, getTagProps) => values.map((value, index) => (
                                <Chip {...getTagProps({ index })} key={value.id} label={value.name} size="small" />
                            ))}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    id="file-product-assignments"
                                    placeholder={isGlobal ? "All products is enabled" : "Search and select products"}
                                    inputProps={{ ...params.inputProps, "aria-label": "Products" }}
                                />
                            )}
                        />
                    </Box>
                )}
                {canAssignContextMeshes && (
                    <Box>
                        <Typography component="label" htmlFor="file-context-mesh-assignments" sx={{ display: "block", fontSize: 12.5, fontWeight: 700, color: T.fg2, mb: 0.75 }}>
                            Context Meshes
                        </Typography>
                        <Autocomplete
                            multiple
                            disableCloseOnSelect
                            disabled={loading}
                            options={contextMeshes}
                            value={selectedContextMeshes}
                            onChange={(_, values) => setPickedContextMeshes(new Set(values.map(value => value.id)))}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            getOptionLabel={option => option.name}
                            noOptionsText="No manageable standalone Context Meshes available"
                            renderTags={(values, getTagProps) => values.map((value, index) => (
                                <Chip {...getTagProps({ index })} key={value.id} label={value.name} size="small" />
                            ))}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    id="file-context-mesh-assignments"
                                    placeholder="Search and select Context Meshes"
                                    inputProps={{ ...params.inputProps, "aria-label": "Context Meshes" }}
                                />
                            )}
                        />
                    </Box>
                )}
                {!loading && !canAssignProducts && !canAssignContextMeshes && (
                    <Alert severity="warning">You do not have permission to change this file’s assignments.</Alert>
                )}
            </Box>

            <Box sx={{ display: "flex", gap: "9px", p: "12px 22px", borderTop: `1px solid ${T.divider}`, backgroundColor: T.bgPanel }}>
                <Pin size={14} color={T.fg4} style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography aria-live="polite" sx={{ fontSize: 11.5, color: T.fg4, lineHeight: 1.5 }}>
                    {assignedCount > 0
                        ? `This file will provide context to ${assignedCount} assignment${assignedCount === 1 ? "" : "s"}.`
                        : "This file will remain in Files without any Product or Context Mesh links."}
                </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", p: "14px 22px", borderTop: `1px solid ${T.divider}` }}>
                <Box component="button" onClick={onClose} disabled={saving} sx={{ height: 38, px: "16px", borderRadius: "9px", border: `1px solid ${T.divider}`, backgroundColor: T.bgPaper, color: T.fg2, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancel
                </Box>
                <Box
                    component="button"
                    onClick={handleSave}
                    disabled={saving || loading || Boolean(loadError) || !hasEditableDomain}
                    sx={{
                        height: 38, px: "18px", borderRadius: "9px", border: "none",
                        backgroundColor: saving || loading || loadError || !hasEditableDomain ? T.divider : T.primary,
                        color: "#fff", fontSize: 13, fontWeight: 600,
                        cursor: saving || loading || loadError || !hasEditableDomain ? "default" : "pointer", fontFamily: "inherit"
                    }}
                >
                    {saving ? "Saving…" : "Save"}
                </Box>
            </Box>
        </Dialog>
    );
};

export default FileScopeModal;
